import { Form } from "@inertiajs/react"
import { Trash2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { useOnline } from "@/hooks/use-online"
import { crewSegmentPath } from "@/routes"

/**
 * OKS-28 — Destructive delete confirmation for the manage page row.
 *
 * **Inertia `<Form>` only — Crew rule.** No shadcn Form, no
 * react-hook-form, no zod. DELETE to `crewSegmentPath(id)`.
 *
 * shadcn `AlertDialog` (NOT `Dialog`) for the destructive-confirm
 * pattern (UX Spec §2.3). Focus defaults to `Cancel` (UX Spec §6 —
 * destructive default-focus posture).
 *
 * **Hard delete** (PRD §Out-of-Scope). Applied-while-deleted edge
 * (a teammate's tab has `?segment=42` open at delete time) is handled
 * BE-side by the apply path's resolver — 404 → redirect to `/crew`
 * + flash. UX Spec §1 edge.
 *
 * **Locked testids:**
 *   - `crew-segments-row-delete-trigger-{id}` — opener Trash button
 *   - `crew-segments-row-delete-dialog-{id}`  — AlertDialog content
 *   - `crew-segments-row-delete-confirm-{id}` — destructive submit
 *   - `crew-segments-row-delete-cancel-{id}`  — cancel Button
 */
export interface SegmentDeleteDialogProps {
  segment: { id: number; name: string }
}

export function SegmentDeleteDialog({ segment }: SegmentDeleteDialogProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid={`crew-segments-row-delete-trigger-${segment.id}`}
            aria-label={t("crew_segments.manage.action_delete")}
          >
            <Trash2 className="size-4" aria-hidden="true" />
          </Button>
        }
      />
      <AlertDialogContent
        data-testid={`crew-segments-row-delete-dialog-${segment.id}`}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{t("crew_segments.delete.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("crew_segments.delete.description", { name: segment.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form
          action={crewSegmentPath(segment.id)}
          method="delete"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <AlertDialogFooter>
              <Button
                type="button"
                variant="ghost"
                data-testid={`crew-segments-row-delete-cancel-${segment.id}`}
                disabled={processing}
                onClick={() => setOpen(false)}
              >
                {t("crew_segments.delete.cancel")}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                data-testid={`crew-segments-row-delete-confirm-${segment.id}`}
                disabled={processing || !online}
              >
                {processing
                  ? t("crew_segments.delete.deleting")
                  : t("crew_segments.delete.confirm")}
              </Button>
            </AlertDialogFooter>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
